// 이 파일은 src/features/admin/ui/AdminDayTimeGrid.tsx에 의도적으로 복제되어 있다(유저 뷰 무손상을 위한 선택).
// formatHour/formatTimeRange, 자정 보정(end.hour()===0 → 24), 최소 높이 28px은 두 파일에서 문자 그대로 동일해야 하므로
// 시간 계산 로직을 바꿀 때는 반드시 반대쪽 파일도 함께 수정한다.
import type { MeetingReservation } from "@features/reservation/model/types";
import { toLosAngelesTime } from "@features/reservation/lib/laTime";
import { clsx } from "clsx";
import { useTranslation } from "react-i18next";

function formatHour(hour: number): string {
  if (hour === 0) return "0 AM";
  if (hour === 12) return "12 PM";
  return hour < 12 ? `${hour} AM` : `${hour - 12} PM`;
}

function formatTimeRange(startTime: string, endTime: string): string {
  const start = toLosAngelesTime(startTime);
  const end = toLosAngelesTime(endTime);
  const fmt = (h: number, m: number) => {
    if (h === 0 && m === 0) return "00:00 AM";
    if (h === 12 && m === 0) return "12:00 PM";
    if (h < 12) return `${h}:${m.toString().padStart(2, "0")} AM`;
    return `${h - 12}:${m.toString().padStart(2, "0")} PM`;
  };
  return `${fmt(start.hour(), start.minute())} - ${fmt(end.hour(), end.minute())}`;
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const ROW_HEIGHT_PX = 56;

export interface DayTimeGridProps {
  reservations?: MeetingReservation[];
}

const DayTimeGrid = ({ reservations = [] }: DayTimeGridProps) => {
  const { t } = useTranslation();
  const reservationBlocks = reservations.map((r) => {
    const start = toLosAngelesTime(r.start_time);
    const end = toLosAngelesTime(r.end_time);
    const startHours = start.hour() + start.minute() / 60;
    const endHoursRaw = end.hour() + end.minute() / 60;
    const endHours = end.hour() === 0 && end.minute() === 0 ? 24 : endHoursRaw;
    const top = startHours * ROW_HEIGHT_PX;
    const height = Math.max((endHours - startHours) * ROW_HEIGHT_PX, 28);

    return {
      ...r,
      top,
      height,
      timeRange: formatTimeRange(r.start_time, r.end_time),
    };
  });

  return (
    <div className="flex gap-3">
      <div className="w-11 shrink-0">
        {HOURS.map((hour) => (
          <div key={hour} className="flex items-center h-14">
            <span className="w-full text-gray-800 text-label2 tracking-[-0.28px]">{formatHour(hour)}</span>
          </div>
        ))}
      </div>
      <div className="relative flex-1">
        {HOURS.map((hour) => (
          <div key={hour} className="flex items-center h-14">
            <div className="w-full h-px bg-gray-200 shrink-0" />
          </div>
        ))}
        <div className="absolute inset-0 top-0 left-0">
          {reservationBlocks
            .filter((r) => !(r.is_mine && r.status === "PENDING"))
            .map((r) => (
              <div
                key={r.id}
                className={clsx(
                  "absolute left-0 right-0 rounded-4 px-3 py-2 text-label3 overflow-hidden flex items-center",
                  r.is_mine ? "bg-primary-500 text-white" : "border border-primary-500 bg-[#FFEEE6]",
                )}
                style={{ top: r.top + 28, height: r.height, minHeight: 28 }}
              >
                {r.is_mine ? t("myReservation") : t("reserved")} ({r.timeRange})
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default DayTimeGrid;
